import GuideContent from "@/containers/Content/components/Guide/GuideContent";
import GuideHead from "@/containers/Content/components/Guide/GuideHead";
import { useViewpostTopObserver } from "@/hooks/useViewpostTopObserver";
import { naverConversionEvent } from "@/utils/naverPageView";
import { Box, Stack } from "@chakra-ui/react";
import {
  ColorIcon,
  colors,
  customEvent,
  getCdnImageSrc,
  HStack,
  Image,
  kakaoPixel,
  pixelCustomEvent,
  PixelEventName,
  responsive,
  tossPixel,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";

export default function PrepareFirst() {
  const router = useRouter();
  const { viewportTopRef } = useViewpostTopObserver();
  const feed = {
    path: "about-promise",
    date: "25-04-28",
    title: "갑자기 오는 장례,\n준비가 없으면 후회만 남습니다",
    description: "",
    imgSrc: "direct/content/prepare/1-background.png",
    keyword: "상조, 장례, 100원 상조",
    contents: [
      {
        type: "p",
        resource: "아버지가 돌아가셨을 때, 장례식장 직원이 이렇게 말했습니다.",
      },
      {
        type: "p",
        resource:
          "👤 “유족분들이 결정하실 건 없어요. 저희가 다 알아서 해드릴게요.”",
      },
      {
        type: "p",
        resource:
          "병원에서 추천해준 곳이기도 했고, 슬픔에 정신도 없던 터라 그대로 믿고 따랐습니다. 그런데 장례 마지막 날, 저희는 <b>400만 원이 넘는 식사비, 수의 50만 원 업셀링, 원하지도 않은 유골함을 강매</b> 당했습니다. 그리고 마지막엔 이렇게 말하더군요.",
      },
      {
        type: "p",
        resource: "👤 “이건 원래 다 드는 비용입니다.”",
      },
      {
        type: "p",
        resource:
          "겪고 나서야 알게 됐습니다. 정말 모든 걸 맡기면, 비용도 판단도 저희 손을 떠난다는 걸요. 아무도 미리 알려주지 않았기에, 저희는 가장 슬퍼야 할 순간에 가장 분노해야 했습니다.",
      },
      {
        type: "img",
        resource: "direct/content/sn/2-2.png",
        imgAlt: "가전제품과 상조",
      },
      {
        type: "h2",
        resource: "그래서 장례 비용에 대해 잘 알고 있는 것이 중요합니다.",
      },
      {
        type: "p",
        resource: "장례는 크게 세 가지 부분에서 비용이 발생합니다.",
      },
      {
        type: "h3",
        resource: "1. 장례식장 비용",
      },
      {
        type: "p",
        resource:
          "장례식장을 이용하며 발생하는 비용으로 빈소, 안치실, 입관실 등의 <b>시설 대여료</b>와 <b>조문객 접대 음식 비용</b>, 그 외 제단 꽃, 영정 사진, 청소 및 관리비등 <b>기타 비용</b>이 포함됩니다.",
      },
      {
        type: "h3",
        resource: "2. 장지 비용",
      },
      {
        type: "p",
        resource:
          "<b>화장비, 봉안당이나 납골당 사용료 등</b>이 포함됩니다. 고인의 주민등록상 주소지와 화장 시설 관할 주소지의 일치 여부에 따라 비용이 달라질 수 있으며, 공설/사설 시설 여부에 따라 비용이 달라질 수 있습니다.",
      },
      {
        type: "h3",
        resource: "3. 상조 서비스 비용",
      },
      {
        type: "p",
        resource:
          "상조는 장례를 치르기 위해 꼭 필요한 <b>인력, 용품, 그리고 차량</b>을 제공합니다. 빈소의 유무와 조문객의 규모에 따라 구성과 비용이 달라집니다.",
      },
      {
        type: "p",
        resource: "",
      },
      {
        type: "h2",
        resource:
          "이 세 가지 비용을 모두 합치면 많게는 천만원이 넘게 들기도 합니다.",
      },
      {
        type: "p",
        resource:
          "그 뿐만 아니라 이 모든 과정이 단 3일 안에 일어나기 때문에, 갑작스러운 상황에 정신없이 결정을 내리다 보면 원하지 않았던 업셀링이나 과도한 청구가 생겨도 “이게 원래 드는 비용인가?” 하고 그냥 넘기기 쉽습니다.",
      },
      {
        type: "p",
        resource: "",
      },
      {
        type: "h2",
        resource: "🧐 그렇다면 장례를 어떻게 준비해야 좋을까요?",
      },
      {
        type: "p",
        resource:
          "어디서 돈이 들고, 어떻게 아낄 수 있는지 정확히 파악할 수 있도록 다음 콘텐츠에서는 불필요한 비용은 줄이고, 좋은 장례를 준비하는 방법을 알려드릴게요.",
      },
    ],
  };

  const trackEvent = () => {
    tossPixel()?.lead();
    naverConversionEvent({ type: "lead" });
    kakaoPixel()?.signUp();
    pixelCustomEvent(PixelEventName.Lead);
  };

  return (
    <VStack width="100%" alignItems="center">
      <div ref={viewportTopRef}></div>
      <GuideHead
        title={feed.title}
        date={"2025년 04월 28일"}
        imgSrc={feed.imgSrc}
      />

      <Stack
        maxWidth={`720px`}
        width="100%"
        css={responsive({
          gap: { base: "28px", md: "40px" },
          padding: { base: "0 0 160px", lg: "60px 0px 160px" },
        })}
      >
        <Box
          fontSize="16px"
          color={colors.gray900}
          backgroundColor={colors.gray50}
          borderRadius="10px"
          padding="10px 20px"
          fontWeight="400"
        >
          💬 다음은 실제 사례를 바탕으로 작성되었습니다.
        </Box>
        <GuideContent contents={feed.contents} isFeed />

        <VStack alignItems="center" margin="20px 0">
          <Box
            fontSize="16px"
            color={colors.gray600}
            backgroundColor={colors.gray50}
            width="fit-content"
            borderRadius="6px"
            padding="10px 20px"
            fontWeight="500"
            cursor="pointer"
            onClick={() => {
              customEvent({
                newGtm: { path: "content", trigger_and_target: "share" },
              });
              navigator.clipboard.writeText(window.location.href);
              alert("링크가 복사되었습니다.");
            }}
          >
            이 컨텐츠 공유하기
          </Box>
        </VStack>

        <VStack gap="30px" css={responsive({ padding: "0 10px" })}>
          <HStack
            alignItems="center"
            gap="10px"
            justifyContent="space-between"
            border={`1px solid ${colors.gray200}`}
            borderRadius="10px"
            css={{ cursor: "pointer" }}
            onClick={() => {
              customEvent({
                newGtm: { path: "content", trigger_and_target: "next_content" },
              });
              trackEvent();
              router.push("/content/feeds/prepare/2/");
            }}
          >
            <Box width="150px" borderRadius="10px 0 0 10px" overflow="hidden">
              <Image
                src={getCdnImageSrc("direct/content/prepare/2-icon.png")}
                alt="이전편"
              />
            </Box>
            <HStack
              justifyContent="space-between"
              width="100%"
              alignItems="center"
              padding="20px 20px 20px 10px"
            >
              <Box>
                <Box className="gray500">다음편 바로보기</Box>
                <Box className="subtitle_18_sb">
                  02. 장례 비용 아끼는 가장 확실한 방법
                </Box>
              </Box>
              <ColorIcon
                name="arrow_right_small"
                color={colors.gray400}
                size={16}
              />
            </HStack>
          </HStack>

          <VStack
            css={{
              borderRadius: "10px",
              border: `1px solid ${colors.gray200}`,
            }}
          >
            <Box
              padding="23px 20px"
              color={colors.gray600}
              backgroundColor={colors.gray50}
              className="subtitle_18_b"
            >
              시리즈 목록
            </Box>
            <HStack
              padding="24px 0px"
              margin="0 20px"
              gap="10px"
              className="subtitle_16_sb orange650"
              css={{
                borderBottom: `1px solid ${colors.gray100}`,
                cursor: "pointer",
              }}
              onClick={() => {
                customEvent({
                  newGtm: { path: "content", trigger_and_target: "prepare_1" },
                });
                router.push("/content/feeds/prepare/1/");
              }}
            >
              <Box>01</Box>
              <Box>갑자기 오는 장례, 준비가 없으면 후회만 남습니다</Box>
            </HStack>
            <HStack
              padding="24px 0px"
              margin="0 20px"
              gap="10px"
              className="body_16_r"
              css={{
                borderBottom: `1px solid ${colors.gray100}`,
                cursor: "pointer",
              }}
              onClick={() => {
                customEvent({
                  newGtm: { path: "content", trigger_and_target: "prepare_2" },
                });
                trackEvent();
                router.push("/content/feeds/prepare/2/");
              }}
            >
              <Box>02</Box>
              <Box>장례 비용 아끼는 가장 확실한 방법</Box>
            </HStack>
            <HStack
              padding="24px 0px"
              margin="0 20px"
              gap="10px"
              className="body_16_r"
              css={{
                borderBottom: `1px solid ${colors.gray100}`,
                cursor: "pointer",
              }}
              onClick={() => {
                customEvent({
                  newGtm: { path: "content", trigger_and_target: "prepare_3" },
                });
                trackEvent();
                router.push("/content/feeds/prepare/3/");
              }}
            >
              <Box>03</Box>
              <Box>좋은 상조는 어떻게 골라야 할까요?</Box>
            </HStack>
          </VStack>
        </VStack>
      </Stack>
    </VStack>
  );
}
